// =============================================================================
// Mobile navigation: a bottom tab bar with four user-chosen pages plus
// "Mehr", which opens a flat, scrollable sheet of every page the user has.
//
// Deliberately flat: the group names in the sheet are labels, not folders —
// one tap always lands on a page. Replaces the old top burger bar, which
// spent a whole row on a single button.
//
// Phones only (below md): from 768px the shell shows a permanent sidebar
// rail instead (App.js), so bar and sheet both hide at md.
// =============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { MoreHorizontal, X, Settings as SettingsIcon } from 'lucide-react';
import { useTranslation } from '../lib/i18n';
import { buildNavSections, resolveTabs } from '../lib/navItems';
import { haptics } from '../lib/haptics';

const BottomNav = ({ data, activeTab, onSelect }) => {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const tabs = useMemo(() => resolveTabs(data), [data]);
  const sections = useMemo(() => buildNavSections(data), [data]);

  // Close the sheet whenever navigation happens (also via the sidebar/desktop).
  useEffect(() => {
    setSheetOpen(false);
  }, [activeTab]);

  const go = (id) => {
    haptics.tap();
    setSheetOpen(false);
    onSelect(id);
  };

  const inTabs = tabs.some((it) => it.id === activeTab);
  const labelOf = (item) => (item.rawLabel ? item.label : t(item.label));

  return (
    <>
      {sheetOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setSheetOpen(false)}
        />
      )}

      {sheetOpen && (
        <div
          className="fixed left-0 right-0 bottom-0 z-40 md:hidden bg-white dark:bg-gray-900 rounded-t-2xl border-t border-gray-200 dark:border-gray-800 flex flex-col"
          style={{ maxHeight: '75vh' }}
        >
          <div className="flex items-center justify-between px-4 pt-3 pb-2 flex-shrink-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {t('nav.allPages')}
            </h3>
            <button
              onClick={() => setSheetOpen(false)}
              className="p-1.5 -mr-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
              aria-label={t('common.close')}
            >
              <X size={18} />
            </button>
          </div>

          <div
            className="overflow-y-auto px-4 pb-2"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)' }}
          >
            {sections.map((section) => (
              <div key={section.groupId} className="mb-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                  {t(section.groupLabel)}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => go(item.id)}
                        className={`flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl border transition-colors ${
                          active
                            ? 'bg-primary-50 dark:bg-gray-800 border-primary-200 dark:border-gray-700 text-primary-700 dark:text-primary-400'
                            : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {Icon && <Icon size={22} />}
                        <span className="text-[10px] leading-tight text-center line-clamp-2 w-full px-0.5">
                          {labelOf(item)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {/* Settings lived in the sidebar, which phones no longer have, so
                it belongs here. Signing out is *not* duplicated here — it sits
                under the account section in Settings, next to deleting the
                account, where a destructive action is harder to hit by
                accident. */}
            <div className="border-t border-gray-200 dark:border-gray-800 pt-3 mt-1 space-y-1">
              <button
                onClick={() => go('settings')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                  activeTab === 'settings'
                    ? 'bg-primary-50 dark:bg-gray-800 text-primary-700 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-200'
                }`}
              >
                <SettingsIcon size={18} />
                {t('app.settings')}
              </button>
            </div>
          </div>
        </div>
      )}

      <nav
        className="fixed left-0 right-0 bottom-0 z-30 md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Columns follow the number of chosen tabs (+ Mehr), so 1, 2 or
            3 tabs stay evenly spaced instead of clumping left. */}
        <div
          className="grid"
          style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}
        >
          {tabs.map((item) => {
            const Icon = item.icon;
            const active = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => go(item.id)}
                className={`flex flex-col items-center gap-0.5 pt-2 pb-1.5 min-w-0 ${
                  active
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {Icon && <Icon size={21} />}
                <span className="text-[10px] leading-none truncate max-w-full px-1">
                  {labelOf(item)}
                </span>
              </button>
            );
          })}
          <button
            onClick={() => {
              haptics.tap();
              setSheetOpen((o) => !o);
            }}
            className={`flex flex-col items-center gap-0.5 pt-2 pb-1.5 ${
              sheetOpen || !inTabs
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            <MoreHorizontal size={21} />
            <span className="text-[10px] leading-none">{t('nav.more')}</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
